/**
 * CompletedChecklistView.js
 *
 * Read-only audit view for viewing completed checklist records.
 * Shows all captured data including signatures, timestamps, and linked context.
 * Designed for audit compliance with clear indication of locked records.
 */

import React from 'react';
import {
  X,
  Check,
  CheckCircle,
  XCircle,
  Lock,
  Calendar,
  Clock,
  Ship,
  User,
  FileText,
  AlertTriangle,
  PenTool,
  Anchor,
  Link2,
  Shield,
  Flame,
  Wind
} from 'lucide-react';
import { CHECKLIST_TYPES, PERMIT_TYPES } from '../../../../schemas/checklistCompletionSchema';
// FT-356: register onClose as the shell-level back handler.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

const CHECKLIST_TYPE_LABELS = {
  [CHECKLIST_TYPES.DAILY_CHECK]: 'Daily Check',
  [CHECKLIST_TYPES.PERMIT_TO_WORK]: 'Permit to Work',
  [CHECKLIST_TYPES.PERIODIC_INSPECTION]: 'Periodic Inspection',
  [CHECKLIST_TYPES.OPERATIONAL]: 'Operational Checklist',
  [CHECKLIST_TYPES.COMPANY]: 'Company Checklist'
};

const PERMIT_TYPE_LABELS = {
  [PERMIT_TYPES.HOT_WORK]: 'Hot Work',
  [PERMIT_TYPES.ENCLOSED_SPACE]: 'Enclosed Space Entry',
  [PERMIT_TYPES.WORKING_ALOFT]: 'Working Aloft',
  [PERMIT_TYPES.WORKING_OVER_SIDE]: 'Working Over Side',
  [PERMIT_TYPES.ELECTRICAL]: 'Electrical Work'
};

const PERMIT_ICONS = {
  [PERMIT_TYPES.HOT_WORK]: Flame,
  [PERMIT_TYPES.ENCLOSED_SPACE]: Wind,
  [PERMIT_TYPES.WORKING_ALOFT]: Shield,
  [PERMIT_TYPES.WORKING_OVER_SIDE]: Anchor,
  [PERMIT_TYPES.ELECTRICAL]: AlertTriangle
};

const CompletedChecklistView = ({ record, onClose }) => {
  // FT-356 — hook must run every render regardless of early return.
  useSetBackHandler(onClose);
  if (!record) return null;

  const completedAt = new Date(record.completedAt);
  const dateString = completedAt.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const timeString = completedAt.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });

  const isPermit = record.checklistType === CHECKLIST_TYPES.PERMIT_TO_WORK;
  const isClosed = record.status === 'closed';
  const PermitIcon = isPermit ? PERMIT_ICONS[record.permitType] || Shield : null;

  // Count passed/failed checks
  const passedCount = record.checks?.filter(c => c.passed).length || 0;
  const totalCount = record.checks?.length || 0;

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 overflow-y-auto">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {isPermit && PermitIcon && (
                <div className={`p-2 rounded-lg ${isClosed ? 'bg-slate-100' : 'bg-amber-100'}`}>
                  <PermitIcon className={`h-5 w-5 ${isClosed ? 'text-slate-600' : 'text-amber-600'}`} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-semibold text-gray-900 truncate">{record.checklistName}</h1>
                <p className="text-sm text-gray-500">
                  {isPermit ? PERMIT_TYPE_LABELS[record.permitType] : CHECKLIST_TYPE_LABELS[record.checklistType]}
                </p>
              </div>
            </div>
          </div>

          {/* Status badges */}
          <div className="flex items-center gap-2 mt-3 ml-14">
            {record._meta?.locked && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                <Lock className="h-3 w-3" />
                Locked for Audit
              </span>
            )}
            {isPermit && (
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                isClosed ? 'bg-slate-100 text-slate-700' : 'bg-green-100 text-green-700'
              }`}>
                {isClosed ? 'Closed' : 'Active'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Context Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sm:px-6">
        {/* Linked Context */}
        {record.linkedContext && (
          <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Link2 className="h-4 w-4 text-cyan-600" />
              </div>
              <div>
                <p className="text-xs text-cyan-600 font-medium">
                  Linked to {record.linkedContext.type === 'voyage' ? 'Voyage' : record.linkedContext.type}
                </p>
                <p className="text-sm font-semibold text-cyan-800">
                  {record.linkedContext.id}
                  {record.linkedContext.name && ` • ${record.linkedContext.name}`}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{dateString}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{timeString}</span>
          </div>
          <div className="flex items-center gap-2">
            <Ship className="h-4 w-4 text-gray-400" />
            <span className="text-gray-900 font-medium">{record.vesselId || 'Unknown Vessel'}</span>
          </div>
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-gray-400" />
            <span className="text-gray-900 font-medium">{record.completedBy?.name || 'Unknown'}</span>
          </div>
        </div>
      </div>

      {/* Content - Add padding for fixed footer */}
      <div className="px-4 py-4 sm:px-6 pb-24">
        {/* Summary Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completion Status</p>
              <p className="text-2xl font-bold text-gray-900">{passedCount} / {totalCount}</p>
            </div>
            <div className={`p-3 rounded-full ${
              passedCount === totalCount ? 'bg-green-100' : 'bg-amber-100'
            }`}>
              {passedCount === totalCount ? (
                <CheckCircle className="h-8 w-8 text-green-600" />
              ) : (
                <AlertTriangle className="h-8 w-8 text-amber-600" />
              )}
            </div>
          </div>
        </div>

        {/* Permit-Specific Details */}
        {isPermit && record.permitDetails && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Permit Details</h3>

            <div className="space-y-3">
              {record.permitDetails.workLocation && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Work Location</span>
                  <span className="font-medium text-gray-900">{record.permitDetails.workLocation}</span>
                </div>
              )}

              {record.permitDetails.workDescription && (
                <div className="text-sm">
                  <span className="text-gray-500">Work Description</span>
                  <p className="font-medium text-gray-900 mt-1">{record.permitDetails.workDescription}</p>
                </div>
              )}

              {record.permitDetails.validityHours && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Validity Period</span>
                  <span className="font-medium text-gray-900">{record.permitDetails.validityHours} hours</span>
                </div>
              )}

              {record.expiresAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Expires At</span>
                  <span className={`font-medium ${
                    new Date(record.expiresAt) < new Date() ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {new Date(record.expiresAt).toLocaleString('en-GB')}
                  </span>
                </div>
              )}

              {/* Enclosed Space specific */}
              {record.permitType === PERMIT_TYPES.ENCLOSED_SPACE && (
                <>
                  {record.permitDetails.o2Level && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">O₂ Level</span>
                      <span className="font-medium text-gray-900">{record.permitDetails.o2Level}%</span>
                    </div>
                  )}
                  {record.permitDetails.lelLevel && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">LEL</span>
                      <span className="font-medium text-gray-900">{record.permitDetails.lelLevel}%</span>
                    </div>
                  )}
                  {record.permitDetails.h2sLevel && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">H₂S Level</span>
                      <span className="font-medium text-gray-900">{record.permitDetails.h2sLevel} ppm</span>
                    </div>
                  )}
                  {record.permitDetails.standbyPerson && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Standby Person</span>
                      <span className="font-medium text-gray-900">{record.permitDetails.standbyPerson}</span>
                    </div>
                  )}
                </>
              )}

              {/* Hot Work specific */}
              {record.permitType === PERMIT_TYPES.HOT_WORK && (
                <>
                  {record.permitDetails.fireWatchDuration && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Fire Watch Duration</span>
                      <span className="font-medium text-gray-900">{record.permitDetails.fireWatchDuration} minutes</span>
                    </div>
                  )}
                  {record.permitDetails.extinguisherLocation && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Extinguisher Location</span>
                      <span className="font-medium text-gray-900">{record.permitDetails.extinguisherLocation}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Checks List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Checklist Items</h3>
          </div>

          <div className="divide-y divide-gray-100">
            {record.checks?.map((check, index) => (
              <div key={check.id || index} className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                    check.passed ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {check.passed ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${check.passed ? 'text-gray-900' : 'text-red-700'}`}>
                      {check.text}
                    </p>
                    {check.notes && (
                      <p className="text-sm text-gray-500 mt-1 italic">
                        Note: {check.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        {record.notes && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-gray-400" />
              <h3 className="font-semibold text-gray-900">Notes</h3>
            </div>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{record.notes}</p>
          </div>
        )}

        {/* Signatures */}
        {(record.signature || record.masterSignature) && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Signatures</h3>

            <div className="space-y-4">
              {record.signature && (
                <div>
                  <p className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                    <PenTool className="h-4 w-4" />
                    {record.completedBy?.name || 'Issuer'} - {record.completedBy?.role || 'Crew'}
                  </p>
                  <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <img
                      src={record.signature}
                      alt="Signature"
                      className="h-16 w-auto"
                    />
                  </div>
                </div>
              )}

              {record.masterSignature && (
                <div>
                  <p className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Master Authorisation
                  </p>
                  <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <img
                      src={record.masterSignature}
                      alt="Master Signature"
                      className="h-16 w-auto"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Closure Record (for closed permits) */}
        {record.closureRecord && (
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 mb-4">
            <h3 className="font-semibold text-slate-900 mb-3">Closure Record</h3>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Closed At</span>
                <span className="font-medium text-slate-900">
                  {new Date(record.closureRecord.closedAt).toLocaleString('en-GB')}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Closed By</span>
                <span className="font-medium text-slate-900">
                  {record.closureRecord.closedBy?.name || 'Unknown'}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-slate-700">Work Completed</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-slate-700">Area Cleared</span>
              </div>

              {record.closureRecord.notes && (
                <div className="text-sm">
                  <span className="text-slate-500">Closure Notes</span>
                  <p className="text-slate-700 mt-1">{record.closureRecord.notes}</p>
                </div>
              )}

              {record.closureRecord.signature && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Closure Signature</p>
                  <div className="border border-slate-200 rounded-lg p-2 bg-white">
                    <img
                      src={record.closureRecord.signature}
                      alt="Closure Signature"
                      className="h-16 w-auto"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Audit Trail */}
        {record._meta && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Audit Information
            </h3>

            <div className="space-y-2 text-xs text-gray-500">
              <div className="flex justify-between">
                <span>Record ID</span>
                <span className="font-mono">{record.id}</span>
              </div>
              <div className="flex justify-between">
                <span>Created</span>
                <span>{new Date(record._meta.createdAt).toLocaleString('en-GB')}</span>
              </div>
              <div className="flex justify-between">
                <span>Version</span>
                <span>{record._meta.version}</span>
              </div>
              <div className="flex justify-between">
                <span>Status</span>
                <span className={record._meta.locked ? 'text-green-600 font-medium' : 'text-amber-600'}>
                  {record._meta.locked ? 'Locked' : 'Unlocked'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-4 py-4 sm:px-6 safe-area-pb">
        <button
          onClick={onClose}
          className="w-full py-3 rounded-xl font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default CompletedChecklistView;
